/** /notification_subscriptions/ — restech notification subscriptions (OCTO
 *  notifications) across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — identical contract to /api_requests/,
 * but over Integration_Notification_Subscription (see
 * NotificationSubscriptionFilterNode in the OpenAPI spec). Small table — no
 * mandatory time bound; rows have integer ids.
 *
 * A subscription row IS the opt-in: creating one mints a per-subscription
 * API_Credential (scope=webhooks) and registers the callback with the vendor.
 * `mode` is the rollout ramp — `log_only` deliveries are authenticated and
 * recorded but not processed; `process` feeds the import pipeline.
 */
import { apiDelete, apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export type SubscriptionStatus =
  | 'pending'
  | 'active'
  | 'create_failed'
  | 'disabled'
  | 'deleting'
  | 'delete_failed';

export type SubscriptionMode = 'log_only' | 'process';

/** ConsoleTenantRef — the tenant reference every console list serialises. */
export interface SubscriptionTenantRef {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  parent_id: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface SubscriptionListItem {
  id: number;
  tenant: SubscriptionTenantRef;
  restech: string;
  /** Account slug; empty string = the default account. */
  account: string;
  /** Canonical uppercase type (BOOKING_UPDATE, PRODUCT_UPDATE, AVAILABILITY_UPDATE;
   *  ORDER_UPDATE is a Ventrata extension). */
  notification_type: string;
  status: SubscriptionStatus;
  /** log_only = deliveries are authenticated + recorded but not processed. */
  mode: SubscriptionMode;
  /** Exact URL registered with the vendor. */
  callback_url: string;
  retry_on_error: boolean;
  remote_id: string | null;
  /** API_Credential.key_fingerprint of the bound credential. */
  credential_fingerprint: string | null;
  last_received_ts: string | null;
  last_error: string | null;
  last_error_ts: string | null;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface SubscriptionCredential {
  id: number;
  name: string;
  key_prefix: string;
  fingerprint: string;
  status: string;
}

export interface SubscriptionDetail extends SubscriptionListItem {
  /** The canonical callback URL derived from the current base URL — differs
   *  from callback_url when the base URL changed since registration or an
   *  override was used (run domain sync to reconcile). */
  callback_url_expected?: string;
  /** Vendor response persisted at registration; auth header values redacted. */
  remote_data?: Record<string, unknown> | null;
  api_credential?: SubscriptionCredential | null;
  /** The vendor has legacy webhooks this subscription type replaces (Ventrata
   *  `octo/webhooks` for BOOKING_UPDATE and ORDER_UPDATE) — gates the legacy-webhooks section
   *  and the coupled mode switches. */
  legacy_webhooks_supported?: boolean;
  /** The per-account quick-reconciliation flag this type maps to (Ventrata);
   *  null when not applicable. */
  reconcile?: ReconcileFlagState | null;
}

export type ReconcileFlagSource = 'account' | 'default_account' | 'schema';

/** Effective values as the reconciler resolves them for the subscription's
 *  account: own config → (named accounts) shared default account → schema default. */
export interface ReconcileFlagState {
  /** e.g. reconcile_bookings_enabled / reconcile_orders_enabled */
  flag: string;
  /** Effective value of `flag` for this account. */
  enabled: boolean;
  /** Value set on this account's own config; null = inherited. */
  explicit: boolean | null;
  source: ReconcileFlagSource;
  /** The restech's master switch (read-only here), e.g. reconcile_enabled. */
  master_flag: string | null;
  master_enabled: boolean;
  master_source: ReconcileFlagSource | null;
  /** enabled AND master_enabled — reconciliation actually runs for this type. */
  in_effect: boolean;
  /** Default account only: named accounts that inherit this value. */
  inherited_by: string[];
}

export interface SubscriptionCreate {
  tour_operator: number;
  restech: string;
  /** Account slug; omit/empty for the default account. */
  account?: string | null;
  notification_type: string;
  mode?: SubscriptionMode;
  retry_on_error?: boolean;
  /** Testing hook: register an arbitrary callback URL (e.g. a webhook
   *  catcher) instead of the canonical one. Audited; shows as drift. */
  callback_url_override?: string | null;
}

/** Key-present semantics; any subset of the fields. */
export interface SubscriptionUpdate {
  mode?: SubscriptionMode;
  /** true → status=disabled (drop deliveries); false → status=active. */
  disabled?: boolean;
}

export interface RemoteStateDifference {
  field: string;
  local: string | null;
  remote: string | null;
}

/** Live vendor-side state of one subscription (detail-drawer fetch). */
export interface SubscriptionRemoteState {
  exists: boolean;
  /** Current vendor payload; callback-auth header values redacted. */
  remote: Record<string, unknown> | null;
  /** null when the vendor does not echo header values. */
  auth_matches: boolean | null;
  differences?: RemoteStateDifference[];
}

/** One legacy vendor webhook for the subscription's event. The vendor has no
 *  "disabled" state: `disabled` = deleted at the vendor from here, with its
 *  full configuration kept on the subscription for the restore. */
export interface LegacyWebhook {
  /** Vendor id (for a disabled one: its id when it was deleted). */
  id: string;
  event: string;
  url: string;
  status: 'active' | 'disabled';
  /** Points at this tenant/account's legacy receive URL. Only managed webhooks
   *  are disabled / restored; others (shared API key) are read-only. */
  managed: boolean;
  /** false when the vendor echoed masked header values — never deleted. */
  restorable: boolean;
  capabilities: string[];
  retry_on_error: boolean | null;
  use_contact_language: boolean | null;
  /** Header names only; every value is `***`. */
  headers: Record<string, string>;
  disabled_ts: string | null;
  disabled_by: { id: number; email: string } | null;
}

export interface LegacyWebhooksState {
  /** false → no legacy webhooks for this restech/type. */
  supported: boolean;
  /** The vendor's legacy event name, e.g. booking_update / order_update. */
  event: string | null;
  webhooks: LegacyWebhook[];
  /** Set when the vendor listing failed: `webhooks` holds only the disabled
   *  ones and the active set is unknown. */
  vendor_error: string | null;
}

export interface LegacyWebhookItemResult {
  id: string;
  url: string | null;
  /** skipped = not deleted because it could not be restored later. */
  result: 'ok' | 'failed' | 'skipped';
  error: string | null;
  /** Enable only: the re-created webhook's vendor id. */
  new_id?: string | null;
}

/** Outcome of one disable / enable run (also the audit payload). */
export interface LegacyWebhookResult {
  event: string;
  /** Every webhook handled and the listing succeeded. */
  ok: boolean;
  /** Run-level failure (e.g. the vendor listing failed). */
  error: string | null;
  webhooks: LegacyWebhookItemResult[];
}

export interface LegacyWebhookOperation {
  subscription: SubscriptionDetail;
  /** null for a plain mode change. */
  webhooks: LegacyWebhookResult | null;
  /** 502 only: why the mode was not changed. */
  detail?: string;
}

export interface SwitchModeRequest {
  mode: SubscriptionMode;
  /** process: switch, then disable the managed legacy webhooks; log_only:
   *  restore them first — the mode changes only when every one came back. */
  legacy_webhooks?: boolean;
}

/** Domain-sync launcher body; all filters optional (default = everything). */
export interface SubscriptionSyncRequest {
  tour_operator_id?: number | null;
  restech?: string | null;
  /** Account slug; empty string targets the default account. */
  account?: string | null;
  /** Reissue every matched subscription's API_Credential. */
  rotate_credentials?: boolean;
  /** Delete vendor-side subscriptions that have no local row (guarded to our
   *  callback namespace; foreign subscriptions are never touched). */
  delete_unmanaged?: boolean;
}

export interface SubscriptionSyncResponse {
  task_id: string;
  task_type: string;
}

export interface SubscriptionListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  /** id|tenant|restech|account|type|status|mode|created_ts|last_received_ts
   *  ('-' desc). Default tenant. */
  ordering?: string;
  /** Authoritative tenant scope, ANDed on top of `filters`. */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
}

function toQuery(p: SubscriptionListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(
  p: SubscriptionListParams = {},
): Promise<PaginatedResponse<SubscriptionListItem>> {
  const qs = toQuery(p);
  return apiGet(`/notification_subscriptions/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

export async function retrieve(id: number | string): Promise<SubscriptionDetail> {
  return apiGet(`/notification_subscriptions/${id}/`);
}

/** POST — row + credential + remote registration. Remote failure still
 *  returns 201 with status=create_failed (use `retry`). */
export async function create(body: SubscriptionCreate): Promise<SubscriptionDetail> {
  return apiPost(`/notification_subscriptions/`, body);
}

export async function update(
  id: number | string,
  body: SubscriptionUpdate,
): Promise<SubscriptionDetail> {
  return apiPatch(`/notification_subscriptions/${id}/`, body);
}

/** DELETE — remote first, then row + credential. A 409 means the remote
 *  delete failed and the row was kept as delete_failed (retry later). */
export async function remove(id: number | string): Promise<void> {
  return apiDelete(`/notification_subscriptions/${id}/`);
}

/** Retry a failed remote registration (pending / create_failed only) —
 *  mints a new API_Credential; on success the old one is deleted. */
export async function retry(id: number | string): Promise<SubscriptionDetail> {
  return apiPost(`/notification_subscriptions/${id}/retry/`);
}

/** Live vendor-side state + diff vs the local row. 502 on vendor error. */
export async function remote(id: number | string): Promise<SubscriptionRemoteState> {
  return apiGet(`/notification_subscriptions/${id}/remote/`);
}

/** Legacy vendor webhooks this subscription replaces (active + disabled).
 *  A failed vendor listing still answers 200 with `vendor_error` set. */
export async function legacyWebhooks(id: number | string): Promise<LegacyWebhooksState> {
  return apiGet(`/notification_subscriptions/${id}/legacy_webhooks/`);
}

/** Mode change, optionally coupled with disabling (→ process) / restoring
 *  (→ log_only) the legacy webhooks. 502 = a restore failed and the mode is
 *  unchanged (the error body is a LegacyWebhookOperation). 409 = another
 *  webhook operation on this subscription is running. */
export async function switchMode(
  id: number | string,
  body: SwitchModeRequest,
): Promise<LegacyWebhookOperation> {
  return apiPost(`/notification_subscriptions/${id}/switch_mode/`, body);
}

/** Set the per-account reconcile flag (only that key, on the subscription's
 *  own account). Audited; returns the refreshed detail. */
export async function setReconcileFlag(
  id: number | string,
  enabled: boolean,
): Promise<SubscriptionDetail> {
  return apiPost(`/notification_subscriptions/${id}/reconcile_flag/`, { enabled });
}

/** Disable the legacy webhooks of a subscription already in process mode. */
export async function disableLegacyWebhooks(id: number | string): Promise<LegacyWebhookOperation> {
  return apiPost(`/notification_subscriptions/${id}/disable_legacy_webhooks/`);
}

/** Launch the domain-sync background task (202 → follow it in /bgtasks). */
export async function sync(body: SubscriptionSyncRequest): Promise<SubscriptionSyncResponse> {
  return apiPost(`/notification_subscriptions/sync/`, body);
}
